/**
 * Centralized Auth API with Cookie-First Approach
 * Reduces dependence on localStorage by preferring httpOnly cookies
 */
import { apiRequest, tokenManager } from './apiClient';
import { API_ENDPOINTS, USES_COOKIES } from '../config/api';

/**
 * Login user
 * Backend sets httpOnly refresh_token cookie automatically
 */
export async function loginUser(email, password) {
  const { data } = await apiRequest.post(
    API_ENDPOINTS.AUTH_LOGIN,
    { email, password }
  );

  // Only store access token in memory/localStorage for non-cookie environments
  // In production, we rely more on httpOnly cookies and refresh flow
  if (data.access_token) {
    tokenManager.set(data.access_token);
  }

  return data;
}

/**
 * Register new user
 * Backend sets httpOnly refresh_token cookie automatically
 */
export async function registerUser(email, password, name, role, phone = null, referralCode = null, dateOfBirth = null, city = null, phoneCountry = 'GB') {
  const body = { email, password, name, role };

  const cleanPhone = String(phone || '').trim();
  if (cleanPhone) body.phone = cleanPhone;
  if (city) body.city = String(city).trim();
  if (phoneCountry) body.phone_country = String(phoneCountry).toUpperCase();

  if (referralCode) body.referral_code = referralCode;

  const cleanDob = String(dateOfBirth || '').trim();
  if (cleanDob) body.date_of_birth = cleanDob;

  const { data } = await apiRequest.post(
    API_ENDPOINTS.AUTH_REGISTER,
    body
  );

  // Only store access token in memory/localStorage for non-cookie environments
  if (data.access_token) {
    tokenManager.set(data.access_token);
  }

  return data;
}

/**
 * Logout user
 * Backend clears httpOnly cookies
 */
export async function logoutUser() {
  try {
    await apiRequest.post(API_ENDPOINTS.AUTH_LOGOUT, {});
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    // Always clear in-memory/localStorage token on logout
    tokenManager.clear();
  }
}

/**
 * Get current user
 * Relies on cookies for auth, token as fallback
 */
export async function getCurrentUser() {
  const { data } = await apiRequest.get(API_ENDPOINTS.AUTH_ME);
  return data;
}

/**
 * Refresh access token
 * Uses httpOnly refresh_token cookie automatically
 */
export async function refreshAccessToken() {
  try {
    const { data } = await apiRequest.post(API_ENDPOINTS.AUTH_REFRESH, {});
    return data;
  } catch (error) {
    // Refresh failed - let interceptor handle cleanup
    throw error;
  }
}

/**
 * Request password reset
 */
export async function requestPasswordReset(email) {
  const { data } = await apiRequest.post(API_ENDPOINTS.AUTH_FORGOT_PASSWORD, { email });
  return data;
}

/**
 * Reset password with token
 */
export async function resetPassword(token, newPassword) {
  const { data } = await apiRequest.post(API_ENDPOINTS.AUTH_RESET_PASSWORD, {
    token,
    new_password: newPassword,
  });
  return data;
}
